import styled, { css } from 'styled-components'
import {
	darkOrange,
	danger,
	dangerHover,
	blackOrange,
	textMuted,
	white,
	warmTint,
	scrim,
} from '@/theme/colors'
import { shadows, radii, space, motion, media, layers } from '@/theme/tokens'
import { text } from '@/theme/typography'
import { flatButton } from '@/theme/recipes'

export type ModalSize = 'small' | 'medium' | 'large'

const widths: Record<ModalSize, string> = { small: '400px', medium: '500px', large: '700px' }

/** Orbit's dialog dim — warm, never neutral black. Sheets from the bottom on phones. */
export const ModalBackdrop = styled.div`
	position: fixed;
	inset: 0;
	z-index: ${layers.modal};
	background: ${scrim};
	display: flex;
	align-items: center;
	justify-content: center;
	padding: ${space[6]};

	${media.phone} {
		padding: ${space[3]};
		align-items: flex-end;
	}
`

export const ModalContainer = styled.div<{ $size: ModalSize; $backgroundColor?: string }>`
	background: ${({ $backgroundColor }) => $backgroundColor || white};
	border-radius: ${radii.card};
	box-shadow: ${shadows.overlay};
	width: 100%;
	max-width: ${({ $size }) => widths[$size]};
	max-height: 90vh;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	position: relative;
	animation: modalIn ${motion.base};

	@keyframes modalIn {
		from {
			opacity: 0;
			transform: translateY(8px) scale(0.98);
		}
		to {
			opacity: 1;
			transform: translateY(0) scale(1);
		}
	}

	${media.phone} {
		max-height: 95vh;
		border-bottom-left-radius: 0;
		border-bottom-right-radius: 0;
		animation: modalSlideUp ${motion.base};
	}

	@keyframes modalSlideUp {
		from {
			opacity: 0;
			transform: translateY(20px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`

/* No divider under the header: the title's weight carries the hierarchy. */
export const ModalHeader = styled.div`
	display: flex;
	align-items: center;
	gap: ${space[3]};
	padding: ${space[7]} ${space[7]} 0;
	flex-shrink: 0;

	${media.phone} {
		padding: ${space[5]} ${space[5]} 0;
	}
`

export const ModalHeaderActions = styled.div`
	display: flex;
	align-items: center;
	gap: ${space[1]};
	margin-left: auto;
	flex-shrink: 0;
`

export const ModalTitle = styled.h2`
	${text.title}
	color: ${blackOrange};
	margin: 0;
	min-width: 0;
`

const headerIconButton = css`
	${flatButton}
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 36px;
	border-radius: ${radii.small};
	color: ${textMuted};
	transition:
		background-color ${motion.fast},
		color ${motion.fast},
		transform ${motion.fast};

	&:hover {
		background: ${warmTint};
		color: ${darkOrange};
	}

	&:active {
		transform: scale(0.97);
	}
`

export const CloseButton = styled.button`
	${headerIconButton}
`

export const DeleteButton = styled.button`
	${headerIconButton}
	color: ${danger};

	&:hover {
		background: ${warmTint};
		color: ${dangerHover};
	}
`

/* The footer owns the bottom padding when it exists, so buttons sit 20px under the body. */
export const ModalBody = styled.div<{ $hasFooter: boolean }>`
	padding: ${space[5]} ${space[7]} ${({ $hasFooter }) => ($hasFooter ? 0 : space[7])};
	flex: 1;
	overflow-y: auto;
	min-height: 0;

	${media.phone} {
		padding: ${space[4]} ${space[5]} ${({ $hasFooter }) => ($hasFooter ? 0 : space[5])};
	}
`

export const ModalFooter = styled.div`
	padding: ${space[5]} ${space[7]} ${space[7]};
	flex-shrink: 0;

	${media.phone} {
		padding: ${space[4]} ${space[5]} ${space[5]};
	}
`
